import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePermission } from '~/hooks/usePermission';
import { api } from '~/redux/api/createApi';

import { EnrichedGroupNode, NodeEnricher } from '../NodeEnricher/NodeEnricher';
import { propertyMapper, TreeBrowser } from './TreeBrowser';
import { TreeBrowserEvent, TreeBrowserNode } from './types';

export interface useTreeBrowserOptions {
  rootNodeId?: string;
  groupId?: string;
}

/*
 * This hook encapsulates the logic for loading a tree of group nodes. It:
 * - Loads the dynamic fields for documents and tickets (this is required
 *   to tell evisearch which fields to return in the search results)
 * - Loads the group and the first level of children for the given root node
 * - Enriches the nodes with the dynamic fields from evisearch
 * - Provides a way to expand/collapse nodes by adding metadata
 * - Provides a way to get the tree of nodes
 *
 */
export const useTreeBrowser = ({
  groupId,
  rootNodeId,
}: useTreeBrowserOptions) => {
  const [nodes, setNodes] = useState<TreeBrowserNode<EnrichedGroupNode>[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const hasDocumentsAccess = usePermission({
    permission: { resourceId: 'documents', resourceType: 'view' },
  });

  const hasTicketAccess = usePermission({
    permission: { resourceId: 'workflow_tickets', resourceType: 'view' },
  });

  const { data: documentDynamicFields } = api.endpoints.getFiltersV2.useQuery(
    {
      type: 'document',
    },
    { skip: !hasDocumentsAccess.hasPermission },
  );

  const { data: ticketDynamicFields } = api.endpoints.getFiltersV2.useQuery(
    {
      type: 'ticket',
    },
    { skip: !hasTicketAccess.hasPermission },
  );

  const nodeEnricher = useMemo(() => {
    if (
      (!documentDynamicFields && hasDocumentsAccess.hasPermission) ||
      (!ticketDynamicFields && hasTicketAccess.hasPermission)
    ) {
      return null;
    }

    return new NodeEnricher(
      documentDynamicFields ?? {},
      ticketDynamicFields ?? {},
      hasDocumentsAccess.hasPermission ?? false,
      hasTicketAccess.hasPermission ?? false,
    );
  }, [
    documentDynamicFields,
    ticketDynamicFields,
    hasDocumentsAccess.hasPermission,
    hasTicketAccess.hasPermission,
  ]);

  const treeBrowser = useMemo(() => {
    if (!rootNodeId || !nodeEnricher || !groupId) {
      return null;
    }

    return new TreeBrowser<EnrichedGroupNode>({
      rootNodeId,
      parentIdMapper: propertyMapper('attributes.parentNodeId'),
      hasChildrenMapper: propertyMapper('attributes.isParent'),
      nodeLoader: nodeEnricher.loadNodeAndEnrinch,
    });
  }, [rootNodeId, nodeEnricher, groupId]);

  const setPageSize = useCallback(
    async (pageSize: number) => {
      if (!treeBrowser) {
        return;
      }

      setIsLoading(true);
      treeBrowser.setPageSize(pageSize);
      await treeBrowser.reload();
      setIsLoading(false);
    },
    [treeBrowser],
  );

  useEffect(() => {
    if (!treeBrowser) {
      return;
    }

    treeBrowser.on(TreeBrowserEvent.CHANGE, () => {
      setNodes(treeBrowser.getTree());
    });
    treeBrowser.on(TreeBrowserEvent.INITIAL_LOAD, () => setIsLoading(false));

    return () => {
      treeBrowser?.removeAllListeners();
    };
  }, [treeBrowser]);

  return {
    documentDynamicFields,
    isLoading,
    nodes,
    ticketDynamicFields,
    treeBrowser,
    setPageSize,
  };
};
